import type { AgeBand, BirthDateSource } from './api.js';

/**
 * Support's date-of-birth correction (RidgeWill/auth#672), the parts with a
 * rule in them. Kept apart from the modal because this portal has no DOM
 * harness: a decision only reachable through a rendered component is one
 * nothing asserts.
 *
 * The three fields (Month, Day, Year) and the readback line match the login
 * app's `BirthDateFields` and the Account portal's modal, so a member reading
 * their date to support over the phone hears it the way they typed it.
 */

export interface BirthDateParts {
  month: string;
  day: string;
  year: string;
}

export const EMPTY_PARTS: BirthDateParts = { month: '', day: '', year: '' };

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const MONTH_OPTIONS = MONTHS.map((label, i) => ({ value: String(i + 1), label }));

/** The ticket reference's limit, as auth enforces it. */
export const TICKET_REFERENCE_MAX = 100;

/** `YYYY-MM-DD` from `2009-03-04`-shaped text, split back into the three fields. */
export function partsOf(iso: string | null | undefined): BirthDateParts {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!m) return EMPTY_PARTS;
  return { year: m[1]!, month: String(Number(m[2])), day: String(Number(m[3])) };
}

/**
 * `YYYY-MM-DD` when the three parts make a real calendar date, else null.
 * Stricter than the login app's, which leaves the check to the server: here
 * the readback needs a real date to say anything, and February 30 should read
 * as a typo before Save rather than as a refusal after it.
 */
export function toIsoDate(v: BirthDateParts): string | null {
  const m = Number(v.month);
  const d = Number(v.day);
  if (!/^\d{4}$/.test(v.year.trim()) || !m || !d) return null;
  const y = Number(v.year);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Whole years old on `today` (UTC calendar day), the way auth counts. */
export function ageOn(iso: string, today: Date): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const ty = today.getUTCFullYear();
  const tm = today.getUTCMonth() + 1;
  const td = today.getUTCDate();
  return ty - y - (tm < m || (tm === m && td < d) ? 1 : 0);
}

export function bandOf(age: number): AgeBand | null {
  if (age < 13) return null;
  if (age < 16) return '13-15';
  if (age < 18) return '16-17';
  return '18+';
}

/** "March 4, 2009", from `2009-03-04`. */
export function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export type Readback =
  | { kind: 'empty' }
  | { kind: 'invalid'; text: string }
  | { kind: 'future'; text: string }
  | { kind: 'under-minimum'; text: string; iso: string }
  | { kind: 'ok'; text: string; iso: string; age: number; band: AgeBand };

/**
 * The live line under the fields: "March 4, 2009. That makes them 17."
 *
 * "Them", not "you": the person typing is staff, correcting somebody else's
 * date. The login app and the Account portal say "you" because there the member
 * types their own.
 *
 * An under-13 date is called out before Save, because auth refuses it here:
 * an account that belongs to a child is closed, not re-dated.
 */
export function readback(parts: BirthDateParts, today: Date): Readback {
  if (!parts.month && !parts.day && !parts.year) return { kind: 'empty' };
  if (!parts.month || !parts.day || parts.year.length < 4) return { kind: 'empty' };
  const iso = toIsoDate(parts);
  if (!iso) return { kind: 'invalid', text: 'That is not a real date. Check the day and month.' };
  const age = ageOn(iso, today);
  if (age < 0) return { kind: 'future', text: `${longDate(iso)} is in the future.` };
  const sentence = `${longDate(iso)}. That makes them ${age}.`;
  const band = bandOf(age);
  if (!band) {
    return {
      kind: 'under-minimum',
      iso,
      text: `${sentence} Auth refuses a date under 13. If the account belongs to a child, close it instead.`,
    };
  }
  return { kind: 'ok', text: sentence, iso, age, band };
}

/** Moving an adult on file to under 18: the dialog warns, since protections change at once. */
export function becomesMinor(current: string | null, next: string, today: Date): boolean {
  if (!current) return false;
  return ageOn(current, today) >= 18 && ageOn(next, today) < 18;
}

/** What a Save click should send, or why Save is off. */
export type CorrectionPlan =
  | { ok: true; body: { dateOfBirth: string; ticketReference?: string } }
  | { ok: false; reason: string };

export function planCorrection(
  parts: BirthDateParts,
  ticketReference: string,
  current: string | null,
  today: Date,
): CorrectionPlan {
  const r = readback(parts, today);
  if (r.kind === 'empty') return { ok: false, reason: 'Enter the month, day and year.' };
  if (r.kind !== 'ok') return { ok: false, reason: r.text };
  if (r.iso === current) return { ok: false, reason: 'That is the date already on file.' };
  const ref = ticketReference.trim();
  if (ref.length > TICKET_REFERENCE_MAX) {
    return { ok: false, reason: `Keep the ticket reference to ${TICKET_REFERENCE_MAX} characters.` };
  }
  return { ok: true, body: ref ? { dateOfBirth: r.iso, ticketReference: ref } : { dateOfBirth: r.iso } };
}

const SOURCE_LABELS: Record<string, string> = {
  signup: 'Sign-up',
  prompt: 'Asked after sign-in',
  member: 'Member',
  support: 'Support',
};

/** How a change's source reads in the history. An unknown source shows as itself. */
export function sourceLabel(source: BirthDateSource | null | undefined): string {
  if (!source) return 'Unknown';
  return SOURCE_LABELS[source] ?? source;
}
